import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Edit3, User, Bot, CheckCircle, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const CATEGORIES = [
  ["payments", "Payments"], ["invoicing", "Invoicing"], ["onboarding", "Onboarding"], ["account", "Account Management"],
  ["compliance", "Compliance"], ["technical", "Technical Issues"], ["other", "Other"],
];

export const CorrectionDialog = ({ open, onOpenChange, selectedLog }) => {
  const [correctedResponse, setCorrectedResponse] = useState("");
  const [category, setCategory] = useState("");
  const [savingCorrection, setSavingCorrection] = useState(false);

  useEffect(() => {
    if (open) {
      setCorrectedResponse("");
      setCategory("");
    }
  }, [open, selectedLog]);

  const handleSaveCorrection = async () => {
    if (!correctedResponse.trim()) {
      toast.error("Please enter the correct response");
      return;
    }
    
    setSavingCorrection(true);
    try {
      await axios.put(`${API}/ai-training/feedback/${selectedLog.log_id}`, {
        corrected_response: correctedResponse,
        category: category || null
      }, { withCredentials: true }).catch(() => {
        // If update fails (feedback doesn't exist), create new one
        return axios.post(`${API}/ai-training/feedback`, {
          webhook_log_id: selectedLog.log_id,
          original_question: selectedLog.message_text,
          original_ai_response: selectedLog.ai_response,
          rating: "negative",
          corrected_response: correctedResponse,
          category: category || null
        }, { withCredentials: true });
      });
      
      toast.success("Correction saved! AI will learn from this.");
      onOpenChange(false);
    } catch (error) {
      toast.error("Failed to save correction");
    } finally {
      setSavingCorrection(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit3 className="w-5 h-5 text-violet-500" />
                Train AI - Add Correction
              </DialogTitle>
              <DialogDescription>
                Provide the correct response. The AI will learn from this for future similar questions.
              </DialogDescription>
            </DialogHeader>
            
            {selectedLog && (
              <div className="space-y-4 py-4">
                {/* Original Question */}
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-start gap-2">
                    <User className="w-4 h-4 text-slate-500 mt-0.5" />
                    <div>
                      <p className="text-xs font-medium text-slate-500">Customer Question</p>
                      <p className="text-sm text-slate-700">{selectedLog.message_text}</p>
                    </div>
                  </div>
                </div>
                
                {/* Original AI Response */}
                <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                  <div className="flex items-start gap-2">
                    <Bot className="w-4 h-4 text-red-500 mt-0.5" />
                    <div>
                      <p className="text-xs font-medium text-red-600">AI Response (Incorrect)</p>
                      <p className="text-sm text-red-700">{selectedLog.ai_response}</p>
                    </div>
                  </div>
                </div>
                
                {/* Corrected Response */}
                <div>
                  <label className="text-sm font-medium text-slate-700">Correct Response</label>
                  <Textarea
                    value={correctedResponse}
                    onChange={(e) => setCorrectedResponse(e.target.value)}
                    placeholder="Enter what the AI should have said..."
                    className="mt-1"
                    rows={4}
                  />
                </div>
                
                {/* Category */}
                <div>
                  <label className="text-sm font-medium text-slate-700">Category (optional)</label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveCorrection}
                disabled={savingCorrection || !correctedResponse.trim()}
                className="gap-2 bg-violet-600 hover:bg-violet-700"
              >
                {savingCorrection ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                Save Correction
              </Button>
            </DialogFooter>
          </DialogContent>
    </Dialog>
  );
};
